import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react'

import { PaginatedData, PaginationReq } from '@/apis/api.types'

import {
  AdminCompletedCourse,
  adminVerifyCourse,
  AttemptedCourseListResDTO,
  Category,
  getAdminCourseDetail,
  getAdminCourses,
  getAttemptedCourseDetail,
  getAttemptedCourses,
  getCoursesCategories,
  getLinkedInAuthorizationUrl,
  getPublishedCourseDetail,
  getPublishedCourses,
  LinkedInCallbackResDTO,
  LinkedStateAndCodeReqDTO,
  PublishedCourseListResDTO,
  sendStateAndCodeToBackend,
  shareCourseCertificate,
  uploadCourseCertificate,
  VerifyCourseReqDTO,
} from '@/apis/courseApis'

export const courseApiSlice = createApi({
  reducerPath: 'coursesApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Courses', 'AdminCourses'],

  endpoints: (builder) => ({
    getCoursesCategories: builder.query<Array<Category>, void>({
      queryFn: async (data) => {
        try {
          return await getCoursesCategories()
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Courses'],
    }),
    getPublishedCourses: builder.query<
      PaginatedData<PublishedCourseListResDTO>,
      PaginationReq<{ category?: number }>
    >({
      queryFn: async (data) => {
        try {
          return await getPublishedCourses(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Courses'],
    }),
    getPublishedCourseDetail: builder.query<PublishedCourseListResDTO, number>({
      queryFn: async (data) => {
        try {
          return await getPublishedCourseDetail(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Courses'],
    }),

    getAttemptedCourses: builder.query<
      PaginatedData<AttemptedCourseListResDTO>,
      PaginationReq<{
        category?: number
        is_completed: boolean
        alumnus_id?: number
      }>
    >({
      queryFn: async (data) => {
        try {
          return await getAttemptedCourses(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Courses'],
    }),
    getAttemptedCourseDetail: builder.query<AttemptedCourseListResDTO, number>({
      queryFn: async (data) => {
        try {
          return await getAttemptedCourseDetail(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Courses'],
    }),

    getAdminCompletedCourses: builder.query<
      PaginatedData<AdminCompletedCourse>,
      PaginationReq<{ status: 'pending' | 'verified' }>
    >({
      queryFn: async (data) => {
        try {
          return await getAdminCourses(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['AdminCourses'],
    }),
    getAdminCompletedCourseDetail: builder.query<AdminCompletedCourse, number>({
      queryFn: async (data) => {
        try {
          return await getAdminCourseDetail(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['AdminCourses'],
    }),

    uploadCertificate: builder.mutation<
      { data: AttemptedCourseListResDTO },
      FormData
    >({
      queryFn: async (data) => {
        try {
          return await uploadCourseCertificate(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['Courses'],
    }),
    adminVerifyCertificate: builder.mutation<
      { data: VerifyCourseReqDTO },
      VerifyCourseReqDTO
    >({
      queryFn: async (data) => {
        try {
          return await adminVerifyCourse(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['AdminCourses'],
    }),
    getLinkedInAuthorizationUrl: builder.query<{ url: string }, void>({
      queryFn: async (data) => {
        try {
          return await getLinkedInAuthorizationUrl()
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Courses'],
    }),
    sendStateAndCodeToBackend: builder.query<
      LinkedInCallbackResDTO,
      LinkedStateAndCodeReqDTO
    >({
      queryFn: async (data) => {
        try {
          return await sendStateAndCodeToBackend(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      providesTags: ['Courses'],
    }),
    shareCourseCertificate: builder.mutation<
      { data: AttemptedCourseListResDTO },
      { id: number; accessToken: string }
    >({
      queryFn: async (data) => {
        try {
          return await shareCourseCertificate(data)
        } catch (error) {
          throw new Error((error as any).response.data.message)
        }
      },
      invalidatesTags: ['Courses'],
    }),
  }),
})

export const {
  usePrefetch: usePrefetchCourses,
  useAdminVerifyCertificateMutation,
  useGetAdminCompletedCourseDetailQuery,
  useGetAdminCompletedCoursesQuery,
  useGetAttemptedCourseDetailQuery,
  useGetAttemptedCoursesQuery,
  useGetLinkedInAuthorizationUrlQuery,
  useGetPublishedCourseDetailQuery,
  useGetPublishedCoursesQuery,
  useSendStateAndCodeToBackendQuery,
  useShareCourseCertificateMutation,
  useUploadCertificateMutation,
  useGetCoursesCategoriesQuery,
} = courseApiSlice
export const initialiseCourses =
  courseApiSlice.endpoints.getPublishedCourses.initiate
export const invalidateApi = courseApiSlice.util.invalidateTags
